/**
 * Keyboard and ARIA wiring for a text input with a list of options under it (the
 * ARIA 1.2 combobox pattern). Up and Down move, Enter picks the highlighted option,
 * Escape closes the list and keeps the text. Focus stays in the input throughout;
 * the highlighted option is announced through aria-activedescendant.
 */
import { useId, useState } from 'react';
import type React from 'react';

interface Options<T> {
  items: T[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (item: T) => void;
  /** Highlight the first option as soon as the list shows (the desk: Enter picks the
   * exact barcode match a scanner sends). Otherwise nothing is highlighted, and Enter
   * falls through to the form. */
  autoHighlight?: boolean;
  /** When this changes (say, the text the list was fetched for), the highlight resets. */
  resetKey?: unknown;
}

export function useCombobox<T>({
  items, open, onOpenChange, onPick, autoHighlight = false, resetKey,
}: Options<T>) {
  const listId = useId();
  const [active, setActive] = useState(-1);
  const count = items.length;

  // New results reset the highlight. Adjusted during render (React's pattern for
  // state that follows an input), not in an effect, so there is no extra pass.
  const signature = `${String(resetKey)}|${count}|${autoHighlight}`;
  const [seen, setSeen] = useState(signature);
  if (seen !== signature) {
    setSeen(signature);
    setActive(autoHighlight && count > 0 ? 0 : -1);
  }

  const shown = open && items.length > 0;
  const optionId = (index: number) => `${listId}-option-${index}`;

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) onOpenChange(true);
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      if (shown && active >= 0 && items[active] !== undefined) {
        event.preventDefault(); // pick, and do not submit the surrounding form
        onPick(items[active]);
      }
    } else if (event.key === 'Escape') {
      if (open) {
        event.preventDefault();
        onOpenChange(false);
      }
    }
  };

  return {
    shown,
    active,
    inputProps: {
      role: 'combobox' as const,
      'aria-autocomplete': 'list' as const,
      'aria-expanded': shown,
      'aria-controls': listId,
      'aria-activedescendant': shown && active >= 0 ? optionId(active) : undefined,
      onKeyDown,
    },
    listProps: { id: listId, role: 'listbox' as const },
    optionProps: (index: number) => ({
      id: optionId(index),
      role: 'option' as const,
      'aria-selected': index === active,
      // Keep focus in the input so the list does not close before the click lands.
      onMouseDown: (event: React.MouseEvent) => event.preventDefault(),
      onMouseEnter: () => setActive(index),
      onClick: () => onPick(items[index]),
    }),
  };
}
